// src/components/Hermes/Tasks/HermesTaskTimeline.tsx
// ============================================
// HermesTaskTimeline - 24 小时执行时间线
//
// 布局：
//   - 横向时间线（00:00 → 24:00），小时刻度（0/6/12/18/24）
//   - 6 个 Hermes 节奏任务按 Cron 时点标注圆点
//   - 今日已执行：实心圆点（绿=成功 / 红=失败）
//   - 未执行：空心圆点（灰）
//   - 当前时间：竖线 + 标签
//   - 悬浮显示任务详情
// ============================================

import { motion } from "framer-motion";
import {
  cronToHour,
  cronToHuman,
  formatTime,
  TASK_ICONS,
  TASK_CN_NAMES,
} from "./types";
import type {
  ScheduledTask,
  TaskExecutionRecord,
} from "../../../runtime/SchedulerRuntime/types";

interface Props {
  tasks: ScheduledTask[];
  history: TaskExecutionRecord[];
  onSelect?: (taskId?: string) => void;
}

/**
 * 判断某任务今日是否已执行，返回今日最近一条记录
 * （history 按时间倒序，find 返回最近一条）
 */
function todayRecord(
  taskId: string,
  history: TaskExecutionRecord[]
): TaskExecutionRecord | undefined {
  const today = new Date().toISOString().slice(0, 10);
  return history.find(
    (h) => h.taskId === taskId && h.startedAt.startsWith(today)
  );
}

export default function HermesTaskTimeline({
  tasks,
  history,
  onSelect,
}: Props) {
  const now = new Date();
  const nowHour = now.getHours() + now.getMinutes() / 60;
  const nowPct = (nowHour / 24) * 100;

  // 小时刻度
  const markers = [0, 6, 12, 18, 24];

  // 空状态
  if (tasks.length === 0) {
    return (
      <div className="text-center py-8 text-[11px] font-mono text-gray-500">
        暂无 Hermes 节奏任务
      </div>
    );
  }

  return (
    <div className="py-2">
      {/* 时间线主体 */}
      <div className="relative h-24 rounded-lg border border-white/5 bg-white/[0.02] px-2">
        {/* 横向轨道 */}
        <div className="absolute left-2 right-2 top-1/2 h-px bg-gradient-to-r from-white/5 via-white/15 to-white/5" />

        {/* 小时刻度 */}
        {markers.map((h) => (
          <div
            key={h}
            className="absolute top-1/2"
            style={{
              left: `calc(${(h / 24) * 100}% )`,
              transform: "translate(-50%, -50%)",
            }}
          >
            <div className="w-px h-3 bg-white/10" />
            <div className="text-[9px] font-mono text-gray-600 mt-0.5 text-center -translate-x-1/2">
              {String(h).padStart(2, "0")}:00
            </div>
          </div>
        ))}

        {/* 当前时间竖线 */}
        <motion.div
          className="absolute top-0 bottom-0 w-px bg-blue-400/60"
          style={{ left: `calc(${nowPct}% )` }}
          animate={{ opacity: [0.4, 1, 0.4] }}
          transition={{ duration: 1.5, repeat: Infinity }}
        >
          <div className="absolute top-0 -translate-x-1/2 text-[9px] font-mono text-blue-400 whitespace-nowrap">
            现在 {formatTime(now.toISOString())}
          </div>
        </motion.div>

        {/* 任务圆点 */}
        {tasks.map((task) => {
          const hour = cronToHour(task.cron);
          if (hour === null) return null;
          const pct = (hour / 24) * 100;
          const rec = todayRecord(task.id, history);
          const executed = !!rec;
          const success = rec?.success;
          const color = !executed
            ? "#6B7280"
            : success
            ? "#34D399"
            : "#F87171";
          const icon = TASK_ICONS[task.id] || "⚙️";
          const name = TASK_CN_NAMES[task.id] || task.name;

          return (
            <div
              key={task.id}
              className="absolute top-1/2 group cursor-pointer"
              style={{
                left: `calc(${pct}%)`,
                transform: "translate(-50%, -50%)",
              }}
              onClick={() => onSelect?.(task.id)}
            >
              {/* 悬浮提示 */}
              <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 hidden group-hover:block z-10 whitespace-nowrap rounded-md border border-white/10 px-2 py-1 shadow-lg"
                   style={{ background: "#131826" }}>
                <div className="text-[11px] text-white">
                  {icon} {name}
                </div>
                <div className="text-[10px] font-mono text-gray-400">
                  {cronToHuman(task.cron)}
                </div>
                <div className="text-[10px] font-mono" style={{ color }}>
                  {executed
                    ? success
                      ? "今日成功"
                      : "今日失败"
                    : "今日未执行"}
                </div>
              </div>
              {/* 圆点 */}
              <div
                className="rounded-full transition-transform group-hover:scale-150"
                style={{
                  width: 12,
                  height: 12,
                  background: executed ? color : "transparent",
                  border: `2px solid ${color}`,
                  boxShadow: executed ? `0 0 8px ${color}80` : "none",
                }}
              />
            </div>
          );
        })}
      </div>

      {/* 图例 */}
      <div className="flex items-center justify-center gap-4 mt-2 text-[10px] font-mono text-gray-500 flex-wrap">
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-emerald-400" /> 成功
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full bg-red-400" /> 失败
        </span>
        <span className="flex items-center gap-1">
          <span className="w-2 h-2 rounded-full border-2 border-gray-500" />{" "}
          未执行
        </span>
        <span className="flex items-center gap-1">
          <span className="w-px h-3 bg-blue-400" /> 当前
        </span>
      </div>
    </div>
  );
}
